// frontend/src/marketing/markdown.ts
//
// Tiny, safe Markdown → HTML renderer for blog post bodies.
//
// We intentionally don't pull in react-markdown / remark / marked — the
// surface we render (admin-authored blog posts) is small and the security
// boundary is "admin-authored, trusted" rather than user-submitted.
//
// What we support:
//   - # H1, ## H2, ### H3
//   - **bold** and *italic*
//   - [text](url) — http(s) only, opens in new tab
//   - `inline code` and ```code blocks```
//   - paragraphs (blank-line separated)
//   - - bullet lists and 1. ordered lists
//   - > blockquotes
//   - --- horizontal rules
//   - ![alt](src) images — http(s) only
//
// What we explicitly do NOT support:
//   - raw HTML (every < and > in the input is escaped)
//   - tables, footnotes, definition lists (use a heading list instead)
//   - javascript:, data:, vbscript: URLs (filtered)
//
// Output is a string of HTML safe to feed into `dangerouslySetInnerHTML`.

const SAFE_URL_RE = /^(https?:\/\/|mailto:|\/[^/])/i;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeUrl(url: string): string {
  const trimmed = url.trim();
  if (!SAFE_URL_RE.test(trimmed)) return '#';
  return trimmed.replace(/"/g, '&quot;');
}

/** Inline transforms: bold, italic, code, links, images. */
function inline(s: string): string {
  // Escape HTML first so user input can never inject tags.
  let out = escapeHtml(s);

  // Inline code — `code`
  out = out.replace(/`([^`]+)`/g, (_m, code) => `<code class="font-mono text-[0.9em] px-1 py-0.5 rounded bg-ink/8 dark:bg-white/10">${code}</code>`);

  // Images — ![alt](src)
  out = out.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt: string, src: string) =>
    `<img alt="${escapeHtml(alt)}" src="${safeUrl(src)}" class="my-6 rounded-2xl w-full" loading="lazy" />`,
  );

  // Links — [text](url)
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text: string, url: string) =>
    `<a href="${safeUrl(url)}" class="text-coral font-semibold underline-offset-2 hover:underline" target="_blank" rel="noopener noreferrer">${text}</a>`,
  );

  // Bold — **text** (do before italic so ** isn't picked up by *)
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-ink dark:text-white">$1</strong>');

  // Italic — *text*
  out = out.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');

  return out;
}

interface Block {
  type: 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'blockquote' | 'pre' | 'hr';
  lines: string[];
}

/**
 * Render Markdown to HTML.
 *
 * @param opts.dropFirstH1  When true, if the first block parsed is an H1
 *                          it is removed before emitting HTML.  Use this on
 *                          article/post pages that already render a styled
 *                          <h1> page-header so the leading "# Title" in
 *                          body_md does not produce a duplicate heading.
 */
export function renderMarkdown(src: string, opts?: { dropFirstH1?: boolean }): string {
  if (!src) return '';
  const lines = src.replace(/\r\n?/g, '\n').split('\n');

  // Pass 1: group into blocks.
  const blocks: Block[] = [];
  let current: Block | null = null;
  let inFence = false;

  function close() {
    if (current) blocks.push(current);
    current = null;
  }

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];

    // Fenced code blocks
    if (raw.trimStart().startsWith('```')) {
      if (!inFence) {
        close();
        current = { type: 'pre', lines: [] };
        inFence = true;
      } else {
        close();
        inFence = false;
      }
      continue;
    }
    if (inFence && current?.type === 'pre') {
      current.lines.push(raw);
      continue;
    }

    // Horizontal rule
    if (/^\s*(---+|\*\*\*+)\s*$/.test(raw)) {
      close();
      blocks.push({ type: 'hr', lines: [] });
      continue;
    }

    // Blank line ends current block (except inside lists, which we close anyway).
    if (raw.trim() === '') {
      close();
      continue;
    }

    // Headings
    const heading = raw.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      close();
      const level = heading[1].length;
      blocks.push({
        type: level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3',
        lines: [heading[2]],
      });
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(raw)) {
      const text = raw.replace(/^>\s?/, '');
      if (current?.type === 'blockquote') current.lines.push(text);
      else { close(); current = { type: 'blockquote', lines: [text] }; }
      continue;
    }

    // Bulleted list
    if (/^[-*]\s+/.test(raw)) {
      const text = raw.replace(/^[-*]\s+/, '');
      if (current?.type === 'ul') current.lines.push(text);
      else { close(); current = { type: 'ul', lines: [text] }; }
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(raw)) {
      const text = raw.replace(/^\d+\.\s+/, '');
      if (current?.type === 'ol') current.lines.push(text);
      else { close(); current = { type: 'ol', lines: [text] }; }
      continue;
    }

    // Paragraph (default)
    if (current?.type === 'p') current.lines.push(raw);
    else { close(); current = { type: 'p', lines: [raw] }; }
  }
  close();

  // Drop redundant leading H1 when the caller already renders a page-header <h1>.
  if (opts?.dropFirstH1 && blocks.length > 0 && blocks[0].type === 'h1') {
    blocks.shift();
  }

  // Pass 2: emit HTML.
  const out: string[] = [];
  for (const b of blocks) {
    switch (b.type) {
      case 'h1':
        out.push(`<h1 class="font-light text-display-md text-ink dark:text-white tracking-[-0.025em] mt-10 mb-4">${inline(b.lines[0])}</h1>`);
        break;
      case 'h2':
        out.push(`<h2 class="font-semibold text-[24px] text-ink dark:text-white tracking-[-0.01em] mt-10 mb-3">${inline(b.lines[0])}</h2>`);
        break;
      case 'h3':
        out.push(`<h3 class="font-semibold text-[19px] text-ink dark:text-white tracking-[-0.005em] mt-8 mb-2">${inline(b.lines[0])}</h3>`);
        break;
      case 'p':
        out.push(`<p class="my-5 text-[16px] leading-[1.75] text-ink/75 dark:text-white/75">${inline(b.lines.join(' '))}</p>`);
        break;
      case 'ul':
        out.push('<ul class="my-5 space-y-2 list-none">');
        for (const item of b.lines) {
          out.push(`<li class="flex items-start gap-2.5 text-[15.5px] text-ink/75 dark:text-white/75 leading-relaxed"><span aria-hidden class="mt-2.5 inline-block h-1 w-1 rounded-full bg-coral shrink-0"></span><span>${inline(item)}</span></li>`);
        }
        out.push('</ul>');
        break;
      case 'ol':
        out.push('<ol class="my-5 space-y-2 list-decimal pl-6">');
        for (const item of b.lines) {
          out.push(`<li class="text-[15.5px] text-ink/75 dark:text-white/75 leading-relaxed">${inline(item)}</li>`);
        }
        out.push('</ol>');
        break;
      case 'blockquote':
        out.push(`<blockquote class="my-6 pl-4 border-l-2 border-coral italic text-ink/70 dark:text-white/70">${inline(b.lines.join(' '))}</blockquote>`);
        break;
      case 'pre':
        out.push(`<pre class="my-6 p-4 rounded-2xl bg-ink-900 text-white overflow-x-auto font-mono text-[13px] leading-relaxed"><code>${escapeHtml(b.lines.join('\n'))}</code></pre>`);
        break;
      case 'hr':
        out.push('<hr class="my-8 border-ink/10 dark:border-white/10" />');
        break;
    }
  }
  return out.join('\n');
}
